import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PhoneNumbersTab from "./PhoneNumbersTab";
import { toast } from "sonner";
import { 
  Building2, 
  Users, 
  Target, 
  CheckSquare,
  TrendingUp,
  Search,
  Ban,
  CheckCircle,
  Eye,
  Crown,
  UserPlus,
  BarChart3,
  Settings,
  MoreVertical,
  LogIn,
  Key,
  Mail,
  GitBranch,
  Loader2,
  User,
  Shield,
  Calendar,
  Trash2,
  Plus,
  Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformAdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgStatus, setOrgStatus] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [userStatus, setUserStatus] = useState("all");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetailOpen, setOrgDetailOpen] = useState(false);
  
  // New state for user management dialogs
  const [setPasswordDialogOpen, setSetPasswordDialogOpen] = useState(false);
  const [hierarchyDialogOpen, setHierarchyDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOrgForHierarchy, setSelectedOrgForHierarchy] = useState(null);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Create user/org dialogs
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: "", email: "", password: "", org_id: "", role: "sales_rep" });
  const [newOrgForm, setNewOrgForm] = useState({ name: "", owner_email: "", plan: "free", seats_purchased: "5" });

  // Change role dialog
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState(null);
  const [changeRoleValue, setChangeRoleValue] = useState("sales_rep");
  const [changeRoleSaving, setChangeRoleSaving] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchOrganizations();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [orgSearch, orgStatus]);

  useEffect(() => {
    fetchUsers();
  }, [userSearch, userStatus]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/platform/stats`, { credentials: "include" });
      if (response.status === 403) {
        toast.error("Access denied. Platform owner only.");
        navigate("/dashboard");
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const params = new URLSearchParams();
      if (orgSearch) params.append("search", orgSearch);
      if (orgStatus !== "all") params.append("status", orgStatus);
      
      const response = await fetch(`${API}/platform/organizations?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations);
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (userSearch) params.append("search", userSearch);
      if (userStatus !== "all") params.append("status", userStatus);
      
      const response = await fetch(`${API}/platform/users?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleSuspendOrg = async (orgId) => {
    if (!window.confirm("Are you sure you want to suspend this organization?")) return;
    
    try {
      const response = await fetch(`${API}/platform/organizations/${orgId}/suspend`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Organization suspended");
        fetchOrganizations();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to suspend organization");
      }
    } catch (error) {
      toast.error("Failed to suspend organization");
    }
  };

  const handleActivateOrg = async (orgId) => {
    try {
      const response = await fetch(`${API}/platform/organizations/${orgId}/activate`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Organization activated");
        fetchOrganizations();
        fetchStats();
      } else {
        toast.error("Failed to activate organization");
      }
    } catch (error) {
      toast.error("Failed to activate organization");
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm("Are you sure you want to suspend this user?")) return;
    
    try {
      const response = await fetch(`${API}/platform/users/${userId}/suspend`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("User suspended");
        fetchUsers();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to suspend user");
      }
    } catch (error) {
      toast.error("Failed to suspend user");
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const response = await fetch(`${API}/platform/users/${userId}/activate`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("User activated");
        fetchUsers();
        fetchStats();
      } else {
        toast.error("Failed to activate user");
      }
    } catch (error) {
      toast.error("Failed to activate user");
    }
  };

  // New handlers for user management
  const handleImpersonateUser = async (user) => {
    if (!window.confirm(`Are you sure you want to login as ${user.name}?`)) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/platform/users/${user.user_id}/impersonate`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const userData = await response.json();
        // Store impersonation data in localStorage with target user name
        localStorage.setItem("impersonation", JSON.stringify({
          ...userData.impersonation,
          target_user_name: userData.name
        }));
        toast.success(`Now logged in as ${user.name}`);
        // Redirect to dashboard as the impersonated user
        window.location.href = "/dashboard";
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to impersonate user");
      }
    } catch (error) {
      toast.error("Failed to impersonate user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendPasswordReset = async (user) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/platform/users/${user.user_id}/send-password-reset`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success(`Password reset email sent to ${user.email}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send password reset");
      }
    } catch (error) {
      toast.error("Failed to send password reset");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/platform/users/${selectedUser.user_id}/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: newPassword })
      });
      
      if (response.ok) {
        toast.success(`Password set for ${selectedUser.name}`);
        setSetPasswordDialogOpen(false);
        setNewPassword("");
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to set password");
      }
    } catch (error) {
      toast.error("Failed to set password");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewHierarchy = async (orgId, orgName) => {
    setSelectedOrgForHierarchy({ org_id: orgId, name: orgName });
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/platform/organizations/${orgId}/hierarchy`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setHierarchyData(data);
        setHierarchyDialogOpen(true);
      } else {
        toast.error("Failed to load hierarchy");
      }
    } catch (error) {
      toast.error("Failed to load hierarchy");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const viewOrgDetails = async (orgId) => {
    try {
      const response = await fetch(`${API}/platform/organizations/${orgId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedOrg(data);
        setOrgDetailOpen(true);
      }
    } catch (error) {
      toast.error("Failed to load organization details");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case "suspended":
        return <Badge className="bg-red-100 text-red-700">Suspended</Badge>;
      case "pending_verification":
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case "pro":
        return <Badge className="bg-blue-100 text-blue-700">Pro</Badge>;
      case "enterprise":
        return <Badge className="bg-purple-100 text-purple-700">Enterprise</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };


  const handleCreateUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/platform/users`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(newUserForm)
      });
      if (res.ok) {
        toast.success("User created");
        setCreateUserOpen(false);
        setNewUserForm({ name: "", email: "", password: "", org_id: "", role: "sales_rep" });
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create user");
      }
    } catch (e) { toast.error("Failed to create user"); }
    finally { setActionLoading(false); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? Their leads and tasks will be preserved (unassigned).`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/platform/users/${userId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("User deleted");
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch (e) { toast.error("Failed to delete user"); }
    finally { setActionLoading(false); }
  };

  const openChangeRoleDialog = (user) => {
    setChangeRoleUser(user);
    const currentRole = user?.organization?.role || "sales_rep";
    setChangeRoleValue(["admin", "sales_manager", "sales_rep"].includes(currentRole) ? currentRole : "sales_rep");
    setChangeRoleDialogOpen(true);
  };

  const handleChangeUserRole = async () => {
    if (!changeRoleUser) return;
    setChangeRoleSaving(true);
    try {
      const res = await fetch(`${API}/platform/users/${changeRoleUser.user_id}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          role: changeRoleValue,
          org_id: changeRoleUser?.organization?.org_id,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Role updated to ${changeRoleValue.replace("_", " ")}`);
        setChangeRoleDialogOpen(false);
        setChangeRoleUser(null);
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to update role");
      }
    } catch (e) {
      toast.error("Failed to update role");
    } finally {
      setChangeRoleSaving(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgForm.name) { toast.error("Organization name is required"); return; }
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/platform/organizations`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ ...newOrgForm, seats_purchased: parseInt(newOrgForm.seats_purchased) })
      });
      if (res.ok) {
        toast.success("Organization created");
        setCreateOrgOpen(false);
        setNewOrgForm({ name: "", owner_email: "", plan: "free", seats_purchased: "5" });
        fetchOrganizations();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create organization");
      }
    } catch (e) { toast.error("Failed to create organization"); }
    finally { setActionLoading(false); }
  };

  const handleDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Delete organization "${orgName}"? This will delete ALL org data (leads, tasks, projects). User accounts are preserved.`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/platform/organizations/${orgId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("Organization deleted");
        fetchOrganizations();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete organization");
      }
    } catch (e) { toast.error("Failed to delete organization"); }
    finally { setActionLoading(false); }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="platform-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Crown className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
              Platform Administration
            </h1>
            <p className="text-slate-500">Manage all organizations and users across the platform</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate("/platform-admin/settings")}
          data-testid="platform-settings-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          Platform Settings
        </Button>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Organizations</p>
                  <p className="text-3xl font-bold">{stats.organizations.total}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {stats.organizations.active} active
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-3xl font-bold">{stats.users.total}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {stats.users.verified} verified
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Leads</p>
                  <p className="text-3xl font-bold">{stats.activity.total_leads}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Recent Signups</p>
                  <p className="text-3xl font-bold">{stats.users.recent_signups}</p>
                  <p className="text-xs text-slate-400 mt-1">Last 7 days</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <UserPlus className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan Distribution */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Subscription Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                <span className="text-sm">Free: {stats.organizations.by_plan.free}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm">Pro: {stats.organizations.by_plan.pro}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-sm">Enterprise: {stats.organizations.by_plan.enterprise}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Organizations and Users */}
      <Tabs defaultValue="organizations">
        <TabsList>
          <TabsTrigger value="organizations">
            <Building2 className="w-4 h-4 mr-2" />
            Organizations
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="phone-numbers" data-testid="phone-numbers-tab">
            <Phone className="w-4 h-4 mr-2" />
            Phone Numbers
          </TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search organizations..."
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    className="pl-10"
                    data-testid="org-search"
                  />
                </div>
                <Select value={orgStatus} onValueChange={setOrgStatus}>
                  <SelectTrigger className="w-[150px]" data-testid="org-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => setCreateOrgOpen(true)} data-testid="create-org-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Org
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Leads</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org) => (
                    <TableRow key={org.org_id} data-testid={`org-row-${org.org_id}`}>
                      <TableCell>
                        <div className="font-medium">{org.name}</div>
                        <div className="text-xs text-slate-500">{org.billing_email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={org.owner?.picture} />
                            <AvatarFallback className="text-xs">
                              {getInitials(org.owner?.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{org.owner?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(org.plan)}</TableCell>
                      <TableCell>{org.member_count || 1}</TableCell>
                      <TableCell>{org.lead_count || 0}</TableCell>
                      <TableCell>{getStatusBadge(org.account_status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewOrgDetails(org.org_id)}
                            data-testid={`view-org-${org.org_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {org.account_status === "active" ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSuspendOrg(org.org_id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`suspend-org-${org.org_id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleActivateOrg(org.org_id)}
                              className="text-emerald-600 hover:text-emerald-700"
                              data-testid={`activate-org-${org.org_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteOrg(org.org_id, org.name)}
                            className="text-red-400 hover:text-red-600"
                            data-testid={`delete-org-${org.org_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {organizations.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No organizations found
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                    data-testid="user-search"
                  />
                </div>
                <Select value={userStatus} onValueChange={setUserStatus}>
                  <SelectTrigger className="w-[180px]" data-testid="user-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending_verification">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => setCreateUserOpen(true)} data-testid="create-user-btn">
                  <Plus className="w-4 h-4 mr-1" /> New User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>System Role</TableHead>
                    <TableHead>Auth</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="text-xs">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {user.name}
                              {user.system_role === "platform_owner" && (
                                <Crown className="w-4 h-4 text-amber-500" />
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{user.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{user.organization?.name || "—"}</div>
                        {user.organization?.plan && (
                          <div className="mt-1">{getPlanBadge(user.organization.plan)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.system_role === "platform_owner" ? (
                          <Badge className="bg-amber-100 text-amber-700">Platform Owner</Badge>
                        ) : (
                          <Badge variant="outline">Subscriber</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {user.auth_provider === "google" ? "Google" : "Email"}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(user.account_status)}</TableCell>
                      <TableCell className="text-right">
                        {user.system_role !== "platform_owner" && (
                          <div className="flex items-center justify-end gap-1">
                            {/* Status toggle */}
                            {user.account_status === "active" ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSuspendUser(user.user_id)}
                                className="text-red-600 hover:text-red-700"
                                title="Suspend User"
                                data-testid={`suspend-user-${user.user_id}`}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            ) : user.account_status === "suspended" ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleActivateUser(user.user_id)}
                                className="text-emerald-600 hover:text-emerald-700"
                                title="Activate User"
                                data-testid={`activate-user-${user.user_id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            ) : null}
                            
                            {/* More actions dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`user-actions-${user.user_id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                  onClick={() => handleImpersonateUser(user)}
                                  data-testid={`impersonate-${user.user_id}`}
                                >
                                  <LogIn className="w-4 h-4 mr-2" />
                                  Login as User
                                </DropdownMenuItem>
                                
                                {user.organization?.org_id && (
                                  <DropdownMenuItem 
                                    onClick={() => handleViewHierarchy(user.organization.org_id, user.organization.name)}
                                    data-testid={`view-hierarchy-${user.user_id}`}
                                  >
                                    <GitBranch className="w-4 h-4 mr-2" />
                                    View Company Hierarchy
                                  </DropdownMenuItem>
                                )}

                                {user.organization?.org_id && user.organization?.role !== "owner" && (
                                  <DropdownMenuItem
                                    onClick={() => openChangeRoleDialog(user)}
                                    data-testid={`change-role-${user.user_id}`}
                                  >
                                    <Shield className="w-4 h-4 mr-2" />
                                    Change Role
                                  </DropdownMenuItem>
                                )}
                                
                                <DropdownMenuSeparator />
                                
                                {user.auth_provider !== "google" && (
                                  <>
                                    <DropdownMenuItem 
                                      onClick={() => handleSendPasswordReset(user)}
                                      data-testid={`reset-password-${user.user_id}`}
                                    >
                                      <Mail className="w-4 h-4 mr-2" />
                                      Send Password Reset
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setSetPasswordDialogOpen(true);
                                      }}
                                      data-testid={`set-password-${user.user_id}`}
                                    >
                                      <Key className="w-4 h-4 mr-2" />
                                      Set Password Manually
                                    </DropdownMenuItem>
                                  </>
                                )}
                                
                                <DropdownMenuSeparator />
                                
                                {user.system_role !== "platform_owner" && (
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteUser(user.user_id, user.name)}
                                    className="text-red-600 focus:text-red-600"
                                    data-testid={`delete-user-${user.user_id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete User
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {users.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No users found
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="phone-numbers">
          <PhoneNumbersTab />
        </TabsContent>
      </Tabs>

      {/* Organization Detail Dialog */}
      <Dialog open={orgDetailOpen} onOpenChange={setOrgDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedOrg?.organization?.name}
            </DialogTitle>
            <DialogDescription>
              Organization details and members
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrg && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{selectedOrg.stats.members}</p>
                  <p className="text-sm text-slate-500">Members</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{selectedOrg.stats.leads}</p>
                  <p className="text-sm text-slate-500">Leads</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{selectedOrg.stats.tasks}</p>
                  <p className="text-sm text-slate-500">Tasks</p>
                </div>
              </div>

              {/* Owner */}
              <div>
                <h4 className="font-medium mb-2">Owner</h4>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Avatar>
                    <AvatarImage src={selectedOrg.owner?.picture} />
                    <AvatarFallback>{getInitials(selectedOrg.owner?.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedOrg.owner?.name}</p>
                    <p className="text-sm text-slate-500">{selectedOrg.owner?.email}</p>
                  </div>
                </div>
              </div>

              {/* Members */}
              <div>
                <h4 className="font-medium mb-2">Members ({selectedOrg.members.length})</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedOrg.members.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.picture} />
                          <AvatarFallback className="text-xs">{getInitials(member.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{member.name}</p>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {member.role?.replace("_", " ")}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={setPasswordDialogOpen} onOpenChange={(open) => {
        setSetPasswordDialogOpen(open);
        if (!open) {
          setNewPassword("");
          setSelectedUser(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Password for {selectedUser?.name}</DialogTitle>
            <DialogDescription>
              Manually set a new password for this user
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                data-testid="new-password-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetPassword} disabled={actionLoading} data-testid="save-password-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog (Platform Owner) */}
      <Dialog
        open={changeRoleDialogOpen}
        onOpenChange={(open) => {
          setChangeRoleDialogOpen(open);
          if (!open) setChangeRoleUser(null);
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="change-role-dialog">
          <DialogHeader>
            <DialogTitle>Change role for {changeRoleUser?.name}</DialogTitle>
            <DialogDescription>
              {changeRoleUser?.organization?.name
                ? <>In organization <span className="font-medium">{changeRoleUser.organization.name}</span></>
                : "Select the new role for this user."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select value={changeRoleValue} onValueChange={setChangeRoleValue}>
                <SelectTrigger data-testid="change-role-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="change-role-option-admin">Admin — full org access</SelectItem>
                  <SelectItem value="sales_manager" data-testid="change-role-option-manager">Sales Manager — manage team & reports</SelectItem>
                  <SelectItem value="sales_rep" data-testid="change-role-option-rep">Sales Rep — leads & calls</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Note: organization Owner role cannot be reassigned here.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleChangeUserRole}
              disabled={changeRoleSaving}
              data-testid="confirm-change-role-btn"
            >
              {changeRoleSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Hierarchy Dialog */}
      <Dialog open={hierarchyDialogOpen} onOpenChange={setHierarchyDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              {selectedOrgForHierarchy?.name} - Company Hierarchy
            </DialogTitle>
            <DialogDescription>
              Organization structure and member details
            </DialogDescription>
          </DialogHeader>
          
          {hierarchyData && (
            <div className="space-y-6 py-4">
              {/* Organization Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-primary">{hierarchyData.stats.total_members}</p>
                    <p className="text-xs text-slate-500">Members</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{hierarchyData.stats.leads}</p>
                    <p className="text-xs text-slate-500">Leads</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{hierarchyData.stats.tasks}</p>
                    <p className="text-xs text-slate-500">Tasks</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{hierarchyData.stats.calls}</p>
                    <p className="text-xs text-slate-500">Calls</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                  <span className="text-slate-500">Plan: {getPlanBadge(hierarchyData.organization.plan)}</span>
                  <span className="text-slate-500">Created: {formatDate(hierarchyData.organization.created_at)}</span>
                </div>
              </div>

              {/* Hierarchy Tree */}
              <div className="space-y-4">
                {/* Owner */}
                {hierarchyData.hierarchy.owner && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-4 h-4 text-amber-500" />
                      <h4 className="font-medium">Owner</h4>
                    </div>
                    <HierarchyMemberCard member={hierarchyData.hierarchy.owner} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                  </div>
                )}

                {/* Admins */}
                {hierarchyData.hierarchy.admins.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-purple-500" />
                      <h4 className="font-medium">Admins ({hierarchyData.hierarchy.admins.length})</h4>
                    </div>
                    <div className="space-y-2 pl-4 border-l-2 border-purple-200">
                      {hierarchyData.hierarchy.admins.map((member) => (
                        <HierarchyMemberCard key={member.user_id} member={member} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Managers */}
                {hierarchyData.hierarchy.managers.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      <h4 className="font-medium">Sales Managers ({hierarchyData.hierarchy.managers.length})</h4>
                    </div>
                    <div className="space-y-2 pl-4 border-l-2 border-blue-200">
                      {hierarchyData.hierarchy.managers.map((member) => (
                        <HierarchyMemberCard key={member.user_id} member={member} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Sales Reps */}
                {hierarchyData.hierarchy.sales_reps.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-slate-500" />
                      <h4 className="font-medium">Sales Reps ({hierarchyData.hierarchy.sales_reps.length})</h4>
                    </div>
                    <div className="space-y-2 pl-4 border-l-2 border-slate-200">
                      {hierarchyData.hierarchy.sales_reps.map((member) => (
                        <HierarchyMemberCard key={member.user_id} member={member} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={newUserForm.name} onChange={e => setNewUserForm({...newUserForm, name: e.target.value})} placeholder="John Smith" data-testid="new-user-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newUserForm.email} onChange={e => setNewUserForm({...newUserForm, email: e.target.value})} placeholder="john@company.com" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={newUserForm.password} onChange={e => setNewUserForm({...newUserForm, password: e.target.value})} placeholder="Strong password" data-testid="new-user-password" />
            </div>
            <div>
              <Label>Organization</Label>
              <Select value={newUserForm.org_id || "_none"} onValueChange={v => setNewUserForm({...newUserForm, org_id: v === "_none" ? "" : v})}>
                <SelectTrigger data-testid="new-user-org"><SelectValue placeholder="No organization" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No organization</SelectItem>
                  {organizations.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUserForm.role} onValueChange={v => setNewUserForm({...newUserForm, role: v})}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                  <SelectItem value="sales_rep">Sales Rep</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateUser} disabled={actionLoading} data-testid="submit-create-user">
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={createOrgOpen} onOpenChange={setCreateOrgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New Organization</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Organization Name *</Label>
              <Input value={newOrgForm.name} onChange={e => setNewOrgForm({...newOrgForm, name: e.target.value})} placeholder="Acme Corp" data-testid="new-org-name" />
            </div>
            <div>
              <Label>Owner Email</Label>
              <Input type="email" value={newOrgForm.owner_email} onChange={e => setNewOrgForm({...newOrgForm, owner_email: e.target.value})} placeholder="owner@company.com (must be existing user)" data-testid="new-org-owner" />
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={newOrgForm.plan} onValueChange={v => setNewOrgForm({...newOrgForm, plan: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Seats</Label>
              <Input type="number" value={newOrgForm.seats_purchased} onChange={e => setNewOrgForm({...newOrgForm, seats_purchased: e.target.value})} data-testid="new-org-seats" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateOrg} disabled={actionLoading} data-testid="submit-create-org">
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper component for hierarchy member cards
function HierarchyMemberCard({ member, formatDate, getStatusBadge }) {
  return (
    <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={member.picture} />
          <AvatarFallback className="text-sm">
            {member.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{member.name}</p>
          <p className="text-sm text-slate-500">{member.email}</p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {getStatusBadge(member.account_status)}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Badge variant="outline" className="text-xs">
            {member.auth_provider === "google" ? "Google" : "Email"}
          </Badge>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {member.last_login ? formatDate(member.last_login) : "Never logged in"}
          </span>
        </div>
      </div>
    </div>
  );
}
